import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVideo,
  faUpload,
  faTrash,
  faSpinner,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { toast, swalConfirm } from '@/lib/swal';

interface Props {
  inspectionId: string;
  findingId: string;
  canUpload: boolean;
  canDelete: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 413) return 'File is too large — maximum video size is 200 MB';
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtBytes(v: string | null | undefined): string {
  if (!v) return '';
  const n = parseInt(v, 10);
  if (isNaN(n)) return '';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function VideoSection({ inspectionId, findingId, canUpload, canDelete }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [isReplacing, setIsReplacing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['finding-video', inspectionId, findingId],
    queryFn: () =>
      inspectionService.getAttachments(inspectionId, {
        attachmentType: 'VIDEO',
        findingId,
        pageSize: 5,
      }),
  });

  const videos = (data?.data ?? []).filter((a) => a.findingId === findingId);
  const video = videos[0];
  const hasVideo = video !== undefined;

  const uploadMutation = useMutation({
    mutationFn: (fd: FormData) => inspectionService.uploadAttachment(inspectionId, fd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-video', inspectionId, findingId] });
      // The readiness panel counts attachments across the whole inspection, so
      // any media change can move a line on it. Without this the panel keeps
      // showing the state from before the upload until something else refetches.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      toast.success('Video uploaded');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to upload video')),
  });

  const replaceMutation = useMutation({
    mutationFn: ({ attId, fd }: { attId: string; fd: FormData }) =>
      inspectionService.replaceAttachment(inspectionId, attId, fd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-video', inspectionId, findingId] });
      // The readiness panel counts attachments across the whole inspection, so
      // any media change can move a line on it. Without this the panel keeps
      // showing the state from before the upload until something else refetches.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      toast.success('Video replaced');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to replace video')),
    onSettled: () => setIsReplacing(false),
  });

  const deleteMutation = useMutation({
    mutationFn: (attId: string) => inspectionService.deleteAttachment(inspectionId, attId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-video', inspectionId, findingId] });
      // The readiness panel counts attachments across the whole inspection, so
      // any media change can move a line on it. Without this the panel keeps
      // showing the state from before the upload until something else refetches.
      void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });
      toast.success('Video removed');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to remove video')),
  });

  const handleFile = (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachmentType', 'VIDEO');
    fd.append('findingId', findingId);
    uploadMutation.mutate(fd);
  };

  const handleReplace = (file: File) => {
    if (!video) return;
    setIsReplacing(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachmentType', 'VIDEO');
    replaceMutation.mutate({ attId: video.id, fd });
  };

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faVideo} className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Finding Video</h2>
          <span className="text-xs text-muted-foreground">(max 1)</span>
        </div>
        <div className="flex items-center gap-1.5">
          {canUpload && hasVideo && (
            <button
              onClick={() => replaceInputRef.current?.click()}
              disabled={replaceMutation.isPending || isReplacing}
              className="flex items-center gap-1.5 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs font-medium text-amber-700 transition hover:bg-amber-500/10 disabled:opacity-50"
            >
              {isReplacing ? (
                <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faArrowsRotate} className="h-3 w-3" />
              )}
              Replace
            </button>
          )}
          {canUpload && !hasVideo && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
            >
              {uploadMutation.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faUpload} className="h-3 w-3" />
              )}
              Upload Video
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/quicktime"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleFile(file);
            e.target.value = '';
          }
        }}
      />

      <input
        ref={replaceInputRef}
        type="file"
        accept="video/mp4,video/quicktime"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            handleReplace(file);
            e.target.value = '';
          }
        }}
      />

      <div className="px-5 py-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading…
          </div>
        ) : hasVideo ? (
          <div className="space-y-3">
            {isReplacing ? (
              <div className="flex h-20 w-full items-center justify-center gap-2 rounded-lg bg-muted text-sm text-muted-foreground">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Replacing video…
              </div>
            ) : video.url ? (
              <video
                src={video.url}
                controls
                className="w-full max-h-72 rounded-lg bg-black"
                aria-label={video.originalFileName}
              />
            ) : null}
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {video.originalFileName}
                </p>
                <p className="text-xs text-muted-foreground">{fmtBytes(video.fileSizeBytes)}</p>
              </div>
              {canDelete && (
                <button
                  onClick={() => {
                    void swalConfirm({
                      title: 'Remove Video?',
                      text: 'This cannot be undone.',
                      confirmText: 'Remove',
                    }).then((ok) => {
                      if (ok) deleteMutation.mutate(video.id);
                    });
                  }}
                  disabled={deleteMutation.isPending}
                  className="flex shrink-0 items-center gap-1.5 rounded-md border border-destructive/30 px-3 py-1.5 text-xs text-destructive transition hover:bg-destructive/5 disabled:opacity-50"
                  aria-label="Remove video"
                >
                  {deleteMutation.isPending ? (
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                  )}
                  Remove
                </button>
              )}
            </div>
          </div>
        ) : canUpload ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex h-20 w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faVideo} className="h-5 w-5" />
            <span className="text-xs">MP4 or MOV · max 200 MB</span>
          </button>
        ) : (
          <p className="text-sm text-muted-foreground">No video uploaded.</p>
        )}
      </div>
    </div>
  );
}
